import { range } from 'lodash'

import { roundToDecimals } from '~/utils/numbers'

// Bridge's rounding for bank transfers: the gross is truncated to cents and the
// fee is rounded up to the next cent. Everything here mirrors that so the form,
// the review screen, and the gross-up all agree to the cent.

// Float noise (10.04 * 100 === 1003.9999999999999) must not move a cent
const CENT_EPSILON = 1e-6

// Truncate to 2 decimal places (Bridge truncates, doesn't round)
export const truncateToCents = (value: number): number =>
  Math.floor(value * 100 + CENT_EPSILON) / 100

// Round fee up to nearest cent (Bridge rounds fees up)
export const ceilToCents = (value: number): number =>
  Math.ceil(value * 100 - CENT_EPSILON) / 100

// Net the bank receives for a gross amount: truncate input, ceil fee, subtract
export const calculateOfframpNetAmount = (
  grossAmount: number,
  feeRate: number,
): number => {
  const truncatedAmount = truncateToCents(grossAmount)
  const fee = ceilToCents(truncatedAmount * feeRate)
  return roundToDecimals(truncatedAmount - fee, 2)
}

// Smallest gross that nets at least `netAmount`. The naive candidate
// net / (1 - rate) can land a cent short once the fee is rounded up, so step
// up a cent at a time until the net covers the target.
export const calculateOfframpGrossAmount = (
  netAmount: number,
  feeRate: number,
): number => {
  const target = truncateToCents(netAmount)
  if (!(target > 0)) return 0
  const candidate = ceilToCents(target / (1 - feeRate))
  return (
    range(0, 5)
      .map((cents) => roundToDecimals(candidate + cents / 100, 2))
      .find((gross) => calculateOfframpNetAmount(gross, feeRate) >= target) ??
    candidate
  )
}
